import React from 'react';
import styles from './Skeleton.module.css';

/**
 * Skeleton Component - Loading placeholder
 *
 * @param {Object} props
 * @param {string} props.variant - 'text' | 'circular' | 'rectangular' | 'rounded'
 * @param {string} props.width - Width (e.g., '100%', '200px')
 * @param {string} props.height - Height (e.g., '20px', '40px')
 * @param {number} props.count - Number of skeleton items to render
 * @param {string} props.className - Additional CSS classes
 */
const Skeleton = ({
  variant = 'text',
  width,
  height,
  count = 1,
  className = '',
  style = {},
  ...rest
}) => {
  const skeletonClasses = [
    styles.skeleton,
    styles[variant],
    className
  ].filter(Boolean).join(' ');

  const skeletonStyle = {
    width: width || (variant === 'text' ? '100%' : undefined),
    height: height || (variant === 'text' ? '1em' : variant === 'circular' ? '40px' : '100px'),
    ...style
  };

  if (count > 1) {
    return (
      <div className={styles.skeletonGroup}>
        {Array.from({ length: count }).map((_, index) => (
          <div
            key={index}
            className={skeletonClasses}
            style={skeletonStyle}
            {...rest}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={skeletonClasses}
      style={skeletonStyle}
      {...rest}
    />
  );
};

/**
 * Preset skeleton components for common use cases
 */
export const SkeletonText = ({ lines = 3, ...props }) => (
  <Skeleton variant="text" count={lines} {...props} />
);

export const SkeletonAvatar = ({ size = '40px', ...props }) => (
  <Skeleton variant="circular" width={size} height={size} {...props} />
);

export const SkeletonButton = ({ ...props }) => (
  <Skeleton variant="rounded" width="120px" height="40px" {...props} />
);

export const SkeletonCard = ({ ...props }) => (
  <div className={styles.skeletonCard}>
    <Skeleton variant="rectangular" height="200px" />
    <div className={styles.skeletonCardContent}>
      <Skeleton variant="text" width="60%" />
      <Skeleton variant="text" width="80%" />
      <Skeleton variant="text" width="40%" />
    </div>
  </div>
);

export const SkeletonTable = ({ rows = 5, columns = 4 }) => (
  <div className={styles.skeletonTable}>
    {/* Header */}
    <div className={styles.skeletonTableRow}>
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton key={i} variant="text" height="20px" />
      ))}
    </div>
    {/* Rows */}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={rowIndex} className={styles.skeletonTableRow}>
        {Array.from({ length: columns }).map((_, colIndex) => (
          <Skeleton key={colIndex} variant="text" height="16px" />
        ))}
      </div>
    ))}
  </div>
);

export const SkeletonList = ({ items = 5 }) => (
  <div className={styles.skeletonList}>
    {Array.from({ length: items }).map((_, index) => (
      <div key={index} className={styles.skeletonListItem}>
        <SkeletonAvatar size="32px" />
        <div className={styles.skeletonListContent}>
          <Skeleton variant="text" width="40%" height="16px" />
          <Skeleton variant="text" width="80%" height="14px" />
        </div>
      </div>
    ))}
  </div>
);

export default Skeleton;
